import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { dashboardAPI, opportunitiesAPI } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import ActivityTimeline from '../../components/common/ActivityTimeline';
import InvestorJourneyProgress from '../../components/common/InvestorJourneyProgress';
import WhatsNextCard from '../../components/common/WhatsNextCard';
import PendingActionsList from '../../components/common/PendingActionsList';
import { 
  TrendingUp, 
  FileText, 
  Wallet, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  ChevronRight,
  Eye,
  Loader2,
  Pen,
  FileSignature
} from 'lucide-react';

const InvestorDashboard = () => {
  const { user } = useAuth();
  const { t, formatCurrency } = useI18n();
  const [stats, setStats] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [signatureRequests, setSignatureRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, oppsRes, commitmentsRes, signaturesRes] = await Promise.all([
        dashboardAPI.investor(),
        opportunitiesAPI.list({ is_featured: true, limit: 3 }),
        api.get('/commitments').catch(() => ({ data: { commitments: [] } })),
        api.get('/signature-requests').catch(() => ({ data: { requests: [] } }))
      ]);
      setStats(statsRes.data);
      setOpportunities(oppsRes.data);
      setCommitments(commitmentsRes.data.commitments || []);
      setSignatureRequests(signaturesRes.data.requests || []);
    } catch (error) {
      // Use default stats
      setStats({
        profile_completion: 40,
        verification_status: 'pending',
        total_committed: 0,
        pending_commitments: 0,
        total_documents: 0,
        open_opportunities: 5
      });
    } finally {
      setLoading(false);
    }
  };

  const getVerificationBadge = (status) => {
    const styles = {
      pending: { variant: 'outline', className: 'border-amber-500/50 text-amber-500', label: 'Pendiente' },
      in_review: { variant: 'outline', className: 'border-blue-500/50 text-blue-500', label: 'En Revisión' },
      approved: { variant: 'default', className: 'bg-emerald-500', label: 'Verificado' },
      rejected: { variant: 'destructive', className: '', label: 'Rechazado' },
      more_info_required: { variant: 'outline', className: 'border-orange-500/50 text-orange-500', label: 'Info Requerida' },
    };
    const style = styles[status] || styles.pending;
    return <Badge variant={style.variant} className={style.className}>{style.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="investor-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="body-text">
            Aquí encontrará un resumen de su actividad de inversión.
          </p>
        </div>
        <Link to="/app/opportunities">
          <Button className="btn-primary gap-2">
            <Eye className="h-4 w-4" strokeWidth={1.5} />
            Ver Oportunidades
          </Button>
        </Link>
      </div>

      {/* What's Next Card - Primary CTA */}
      <WhatsNextCard 
        user={{...user, profile_completion: stats?.profile_completion || 0}} 
        commitments={commitments} 
        signatureRequests={signatureRequests} 
      />

      {/* Investor Journey Progress */}
      {user?.verification_status === 'approved' && (
        <Card className="card-dashboard">
          <CardContent className="p-4">
            <InvestorJourneyProgress user={user} commitments={commitments} />
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              {t('dashboard.profileCompletion')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between mb-2">
              <span className="text-3xl font-light">{stats?.profile_completion || 0}%</span>
            </div>
            <Progress value={stats?.profile_completion || 0} className="h-1.5" />
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              {t('dashboard.verificationStatus')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              {stats?.verification_status === 'approved' ? (
                <CheckCircle2 className="h-8 w-8 text-emerald-500" strokeWidth={1.5} />
              ) : (
                <Clock className="h-8 w-8 text-amber-500" strokeWidth={1.5} />
              )}
              {getVerificationBadge(stats?.verification_status)}
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Capital Comprometido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Wallet className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="text-2xl font-light">{formatCurrency(stats?.total_committed || 0)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              {t('dashboard.openOpportunities')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="text-2xl font-light">{stats?.open_opportunities || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Featured Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Pending Actions List */}
        <PendingActionsList 
          user={{...user, profile_completion: stats?.profile_completion || 0}} 
          commitments={commitments} 
          signatureRequests={signatureRequests} 
        />

        {/* Featured Opportunities */}
        <Card className="card-dashboard lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Oportunidades Destacadas</CardTitle>
            <Link to="/app/opportunities">
              <Button variant="ghost" size="sm" className="gap-1">
                Ver todas
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {opportunities.length > 0 ? opportunities.slice(0, 3).map((opp) => (
                <Link 
                  key={opp.opportunity_id} 
                  to={`/app/opportunities/${opp.slug}`}
                  className="flex items-center justify-between p-4 rounded-sm border border-border hover:border-primary/30 transition-colors"
                >
                  <div>
                    <p className="font-medium mb-1">{opp.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {opp.location_city} • Min. {formatCurrency(opp.minimum_investment)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-primary font-medium">{opp.projected_irr_base}-{opp.projected_irr_upside}%</p>
                    <p className="text-xs text-muted-foreground">TIR Objetivo</p>
                  </div>
                </Link>
              )) : (
                <div className="text-center py-8 text-muted-foreground">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  <p>No hay oportunidades destacadas disponibles</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Activity Timeline */}
        <ActivityTimeline 
          title="Tu Actividad Reciente"
          emptyMessage="Aún no hay actividad registrada. Completa tu perfil para comenzar."
          limit={5}
        />
      </div>

      {/* Risk Disclaimer */}
      <Card className="border-muted bg-muted/30">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">
            <strong>Aviso:</strong> La inversión en bienes raíces conlleva riesgos, incluyendo la posible pérdida de capital. Los rendimientos pasados no garantizan resultados futuros. Consulte todos los documentos del proyecto antes de invertir.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestorDashboard;
